import axios from 'axios'
import { resolveBackendErrorMessage, notifyApiError } from './apiError'
import { config } from './config'
import { PATHS } from './routes'
import { resolveClientFeature } from './feature/resolveClientFeature'
import { FEATURE_QUERY_PARAM } from './feature/feature.constants'

const api = axios.create({
  baseURL: config.apiBaseUrl,
  timeout: 15_000,
  headers: {
    'Content-Type': 'application/json',
  },
})

// ---------------------------------------------------------------------------
// Auth token — set by AuthContext once the session is available client-side.
// All subsequent API requests will include Authorization: Bearer <token>.
// ---------------------------------------------------------------------------

let _authToken: string | null = null

/** Called by AuthContext whenever the session token changes. */
export function setAuthToken(token: string | null): void {
  _authToken = token
}

const SUPPORTED_LOCALES = ['es', 'en', 'pt']

function getLocale(): string {
  try {
    const stored = localStorage.getItem('locale')
    if (stored && SUPPORTED_LOCALES.includes(stored)) return stored
  } catch {
    // localStorage unavailable (sandboxed context)
  }
  return 'es'
}

// Inject Bearer token, Accept-Language, y `?feature=<env>` en cada request.
// El feature env identifica el namespace de K8s al que tiene que rutear el
// ingress — sin el, las llamadas caen al pod default. Se resuelve client-
// side desde la URL o sessionStorage (resolveClientFeature).
api.interceptors.request.use((config) => {
  if (_authToken) {
    config.headers.Authorization = `Bearer ${_authToken}`
  }
  config.headers['Accept-Language'] = getLocale()

  const feature = resolveClientFeature()
  if (feature) {
    // axios.params es preferible a manipular config.url porque maneja la
    // serializacion (encode, ?/& separator) automaticamente y respeta los
    // params que el caller ya haya pasado en la request original.
    // IMPORTANTE: URLSearchParams no tiene propiedades enumerables propias,
    // por lo que el spread {...urlSearchParams} da {} y pierde todos los params.
    // Si ya es URLSearchParams, mutamos la instancia existente.
    if (config.params instanceof URLSearchParams) {
      config.params.set(FEATURE_QUERY_PARAM, feature)
    } else {
      config.params = { ...(config.params ?? {}), [FEATURE_QUERY_PARAM]: feature }
    }
  }
  return config
})

// Surface backend error message via toast and handle 401 redirect.
// Opt-outs per request:
//   { meta: { skipErrorToast:    true } } — suppress the toast
//   { meta: { skipAuthRedirect:  true } } — keep the user on the page on 401
api.interceptors.response.use(
  res => res,
  err => {
    const meta = err?.config?.meta ?? {}

    // Async: si la request era `responseType: 'blob'` (descargas), el body de
    // error del backend viene envuelto en un Blob y hay que leerlo antes de
    // poder extraer el Message real — no bloquea el reject de abajo.
    if (!meta.skipErrorToast) {
      void resolveBackendErrorMessage(err).then(notifyApiError)
    }

    if (
      err.response?.status === 401 &&
      !meta.skipAuthRedirect &&
      typeof window !== 'undefined'
    ) {
      window.location.href = PATHS.login
    }
    return Promise.reject(err)
  },
)

export default api
